import { Component, OnInit } from '@angular/core';
import { AuthConfig, AuthService } from 'src/app/Services/auth.service';
import { DashboardService } from 'src/app/Services/dashboard.service';
import { ArtistDashboard } from 'src/app/models/response/artistdashboard.models';

/**
 * TEMPORARY nav-migration scaffolding.
 *
 * A hidden, role-aware "directory" of every page so nothing is unreachable while we
 * rebuild the navigation (icon rail + bottom bar). For the artist, detail/param pages
 * use a live sample id (their first artwork / gallery) so they are genuinely clickable.
 * Gallery + admin list pages link directly; their param/detail pages are reached via
 * the list they belong to (noted "open from …"). A memory jog, not a long-term feature
 * — remove once the new nav fully covers every page. Mirrors NAVIGATION-INVENTORY.md.
 */
interface PageLink { label: string; path: any[]; note?: string; disabled?: boolean; }
interface PageGroup { title: string; icon: string; links: PageLink[]; }

@Component({
    selector: 'app-all-pages',
    templateUrl: './all-pages.component.html',
    styleUrls: ['./all-pages.component.css'],
    providers: [DashboardService],
    standalone: false
})
export class AllPagesComponent implements OnInit {
    public groups: PageGroup[] = [];
    public roleLabel = '';
    public loaded = false;
    private artistId = '';
    private username = '';
    private sampleArtworkId: string | null = null;
    private sampleGalleryId: string | null = null;

    constructor(private authService: AuthService, private dashboardService: DashboardService) { }

    public ngOnInit(): void {
        const cfg: AuthConfig = this.authService.getAuthConfig();
        this.artistId = this.authService.getUserId();
        this.username = this.authService.getUserName();

        if (cfg.IsGallery || cfg.IsGalleryStaff) {
            this.roleLabel = 'Gallery';
            this.buildGallery();
            this.loaded = true;
        } else if ((cfg.IsSuperAdmin || cfg.IsAdministrator) && !cfg.IsArtist) {
            this.roleLabel = 'Admin';
            this.buildAdmin();
            this.loaded = true;
        } else {
            this.roleLabel = 'Artist';
            this.dashboardService.getArtistDashboard().subscribe({
                next: (d: ArtistDashboard) => {
                    this.sampleArtworkId = d.NewProductsAdded?.[0]?.ProductId ?? null;
                    this.sampleGalleryId = d.Galleries?.[0]?.GalleryId ?? null;
                    this.buildArtist();
                    this.loaded = true;
                },
                error: () => { this.buildArtist(); this.loaded = true; }
            });
        }
    }

    private link(path: any[], label: string, note?: string): PageLink {
        return { label, path, note };
    }
    private viaList(label: string, list: string): PageLink {
        return { label, path: [], note: `open from ${list}`, disabled: true };
    }
    private verify(path: any[], label: string): PageLink {
        return { label, path, note: 'verify it resolves' };
    }

    private buildArtist(): void {
        const art = this.sampleArtworkId;
        const gal = this.sampleGalleryId;
        const id = this.artistId;
        const user = this.username || id;
        this.groups = [
            { title: 'Home', icon: 'fa fa-home', links: [
                this.link(['/ds-dashboard'], 'Dashboard'),
                this.link(['/artist-onboarding-checklist'], 'Onboarding checklist'),
                this.link(['/dashboard'], 'Legacy dashboard'),
            ]},
            { title: 'Artworks', icon: 'fa fa-paint-brush', links: [
                this.link(['/artists', 'artworks'], 'Your artworks (list)'),
                this.link(['/artists', 'artworks', 'create'], 'Add an artwork'),
                art ? this.link(['/artists', 'artworks', art], 'Artwork detail / editor', 'sample artwork') : this.viaList('Artwork detail / editor', 'the artworks list'),
                this.link(['/artists', 'art-wotk-categories'], 'Sales categories'),
                this.link(['/artists', 'product-series'], 'Product series'),
                this.link(['/artists', 'product-material'], 'Product materials'),
                this.link(['/artists', 'product-glazes'], 'Product features'),
            ]},
            { title: 'Website', icon: 'fa fa-globe', links: [
                this.link(['/artists', 'portfolio'], 'Portfolio / website builder'),
                this.link(['/profile', user], 'Public profile (My Website)'),
            ]},
            { title: 'Galleries', icon: 'fa fa-university', links: [
                this.link(['/artists', 'connected-galleries'], 'Connected galleries'),
                this.link(['/artists', 'product-requests'], 'Product requests'),
                this.link(['/view-proposals'], 'Proposals'),
                gal ? this.link(['/artists', 'gallery', gal, 'gallery-products'], "A gallery's products", 'sample gallery') : this.viaList("A gallery's products", 'Connected galleries'),
                this.viaList('Connection request', 'Connected galleries'),
            ]},
            { title: 'Events', icon: 'fa fa-trophy', links: [
                this.link(['/events', 'my-participated-events'], 'My events'),
                this.link(['/events', 'available'], 'Available events'),
                this.link(['/events', 'create-artwork'], 'Create simple artwork (event)'),
                this.viaList('Submit artworks (event)', 'an event'),
                this.viaList('Submitted artworks / payment', 'an event'),
            ]},
            { title: 'Selling', icon: 'fa fa-tag', links: [
                this.link(['/view-consignments'], 'Consignments (list)'),
                this.link(['/artists', 'add-consignment'], 'Add consignment'),
                this.viaList('Consignment detail', 'the consignments list'),
                this.link(['/sync-products'], 'Sync products'),
                this.link(['/labels-generation'], 'Labels generation'),
                this.link(['/connect-with-shopify-account'], 'Connect Shopify'),
                this.link(['/connect-with-squarespace-account'], 'Connect Squarespace'),
                this.link(['/connect-with-woocommerce-account'], 'Connect WooCommerce'),
            ]},
            { title: 'Marketing', icon: 'fa fa-bullhorn', links: [
                this.link(['/social-media'], 'Social media shares'),
                this.link(['/facebook-accounts'], 'Facebook accounts'),
                this.link(['/instagram-account'], 'Instagram accounts'),
                this.link(['/connect-with-facebook'], 'Connect Facebook'),
                this.link(['/connect-with-instagram'], 'Connect Instagram'),
                this.link(['/delete-facebook-meta-data'], 'Delete Facebook data'),
                this.link(['/delete-instagram-meta-data'], 'Delete Instagram data'),
            ]},
            { title: 'Messages', icon: 'fa fa-comments', links: [
                this.link(['/inbox'], 'Inbox'),
            ]},
            { title: 'You & Settings', icon: 'fa fa-cog', links: [
                this.link(['/edit-profile'], 'Edit profile'),
                this.link(['/user-invoice'], 'My subscriptions / invoices'),
                this.link(['/notifications-list'], 'Notifications'),
                this.link(['/request-deletion'], 'Request account deletion'),
            ]},
            { title: 'Public / shared', icon: 'fa fa-link', links: [
                art ? this.link(['/', id, 'artworks', art], 'Public artwork view', 'sample artwork') : this.viaList('Public artwork view', 'an artwork'),
                this.viaList('Shareable portfolio link', 'Portfolio'),
            ]},
        ];
    }

    private buildGallery(): void {
        this.groups = [
            { title: 'Home', icon: 'fa fa-home', links: [
                this.link(['/ds-gallery-dashboard'], 'Dashboard'),
                this.link(['/gallery-onboarding-checklist'], 'Onboarding checklist'),
                this.link(['/dashboard'], 'Legacy dashboard'),
            ]},
            { title: 'Artworks', icon: 'fa fa-paint-brush', links: [
                this.link(['/galleries', 'gallery-products'], 'Consignment products (inventory)'),
                this.link(['/galleries', 'view-gallery-products'], 'Gallery products'),
                this.link(['/galleries', 'gallery-product', 'create'], 'Create a gallery product'),
                this.viaList('Gallery product detail', 'Gallery products'),
                this.link(['/galleries', 'requested-products'], 'Requested products'),
                this.link(['/labels-generation'], 'Labels generation'),
            ]},
            { title: 'Artists & proposals', icon: 'fa fa-university', links: [
                this.link(['/galleries', 'connected-artists'], 'Connected artists'),
                this.link(['/galleries', 'view-proposals'], 'Proposals'),
                this.viaList('Proposal selection', 'Proposals'),
                this.viaList('View an artist', 'Connected artists'),
                this.viaList('Connection request', 'Connected artists'),
            ]},
            { title: 'Website', icon: 'fa fa-globe', links: [
                this.link(['/galleries', 'portfolio'], 'Gallery portfolio'),
                this.link(['/galleries', 'gallery-shareable-product'], 'Shareable products'),
            ]},
            { title: 'Events', icon: 'fa fa-trophy', links: [
                this.link(['/events'], 'Gallery events (list)'),
                this.link(['/events', 'create'], 'Create an event'),
                this.viaList('Edit an event', 'the events list'),
                this.viaList('View an event', 'the events list'),
            ]},
            { title: 'Selling', icon: 'fa fa-tag', links: [
                this.link(['/view-consignments'], 'Consignments (list)'),
                this.viaList('Consignment product selection', 'a consignment'),
                this.link(['/product-sync'], 'Product sync'),
                this.link(['/connect-with-shopify-account'], 'Connect Shopify'),
                this.link(['/connect-with-squarespace-account'], 'Connect Squarespace'),
                this.link(['/connect-with-woocommerce-account'], 'Connect WooCommerce'),
                this.link(['/connect-squareup-pos-account'], 'Connect Square POS'),
                this.link(['/connect-with-wix-account'], 'Connect Wix'),
            ]},
            { title: 'Marketing', icon: 'fa fa-bullhorn', links: [
                this.link(['/social-media'], 'Social media shares'),
                this.link(['/facebook-accounts'], 'Facebook accounts'),
                this.link(['/instagram-account'], 'Instagram accounts'),
                this.link(['/connect-with-facebook'], 'Connect Facebook'),
                this.link(['/connect-with-instagram'], 'Connect Instagram'),
            ]},
            { title: 'Messages', icon: 'fa fa-comments', links: [
                this.link(['/inbox'], 'Inbox'),
            ]},
            { title: 'You & Settings', icon: 'fa fa-cog', links: [
                this.link(['/gallery-staff'], 'Gallery staff'),
                this.link(['/edit-profile'], 'Edit / gallery profile'),
                this.link(['/user-invoice'], 'My subscriptions / invoices'),
                this.link(['/notifications-list'], 'Notifications'),
            ]},
        ];
    }

    private buildAdmin(): void {
        this.groups = [
            { title: 'Home', icon: 'fa fa-home', links: [
                this.link(['/ds-admin-dashboard'], 'Dashboard'),
                this.link(['/dashboard'], 'Legacy dashboard'),
            ]},
            { title: 'Users', icon: 'fa fa-users', links: [
                this.link(['/admin', 'view'], 'Artists'),
                this.link(['/admin', 'view-galleries'], 'Galleries'),
                this.verify(['/admins'], 'Admins'),
                this.viaList('View an artist', 'Artists'),
            ]},
            { title: 'Communication', icon: 'fa fa-envelope', links: [
                this.link(['/admin', 'email-events'], 'Email & SMS events'),
                this.link(['/admin', 'email-templates'], 'Email templates'),
                this.link(['/admin', 'sms-templates'], 'SMS templates'),
                this.link(['/admin', 'system-events-workflow'], 'Event workflow'),
                this.link(['/admin', 'email-track-log'], 'Email logs'),
            ]},
            { title: 'Platform & settings', icon: 'fa fa-cog', links: [
                this.link(['/admin', 'art-type'], 'Art types'),
                this.verify(['/subscription-rules'], 'Subscription rules'),
                this.verify(['/system-settings'], 'System settings'),
                this.link(['/admin', 'application-log'], 'Application logs'),
                this.link(['/edit-profile'], 'My profile'),
            ]},
            { title: 'Product setup', icon: 'fa fa-sliders', links: [
                this.link(['/artists', 'art-wotk-categories'], 'Sales categories'),
                this.link(['/artists', 'product-series'], 'Product series'),
                this.link(['/artists', 'product-material'], 'Product materials'),
                this.link(['/artists', 'product-glazes'], 'Product features'),
            ]},
        ];
    }
}
